import React from 'react';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import { OrganizationTypes } from '../../../../../../../primary-record-types';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { clientRecordStatusTypeGuard } from '../../../../../../../utils/clientRecordsUtil';
import { getIntl } from '../../../../../../../utils/intl';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';
import getName from '../../../../../../../utils/getName';

const intl = getIntl('archive-client-prompt-content', {
    archiveClient: 'Archive Client',
    archiveClientPrompt: 'Are you sure you want to archive {clientName}?',
    thisClient: 'this client',
    archive: 'Archive',
    cancel: 'Cancel',
});

export type ArchiveClientPromptContentOwnProps = {
    onClose: () => void;
    organizationClientRecordId: string | undefined;
    onArchive: (organizationClientRecordId: string) => void;
};

export type ArchiveClientPromptContentProps = ArchiveClientPromptContentOwnProps;

const Content: React.FC<{
    clientRecord: OrganizationTypes.OrganizationClientRecordForStatus<'not-invited'>;
    onReEngageClientRecord: () => void;
    onClose: () => void;
}> = ({ clientRecord, onReEngageClientRecord, onClose }) => (
    <ContentStack>
        <InfoIcon
            alertIconProps={{
                name: 'Archive',
                variant: 'info',
            }}
            title={intl('archiveClient')}
            subText={intl('archiveClientPrompt', {
                clientName: getName(clientRecord.clientDetails) || intl('thisClient'),
            })}
        />
        <PromptActions
            actions={[
                {
                    title: intl('archive'),
                    onPress: onReEngageClientRecord,
                    icon: 'Archive',
                    variant: 'primary',
                    testID: 'confirm-archive-client',
                },
                {
                    title: intl('cancel'),
                    onPress: onClose,
                    variant: 'secondary',
                    icon: 'XCircle',
                    testID: 'cancel-archive-client',
                },
            ]}
        />
    </ContentStack>
);

const ArchiveClientPromptContent: React.FC<ArchiveClientPromptContentProps> = ({
    onClose,
    organizationClientRecordId,
    onArchive,
}) => {
    const { isLoadingClientRecord, clientRecord, isArchivingClientRecord } = useOrganizationClientRecords({
        organizationClientRecordId,
    });

    const isLoading = isLoadingClientRecord || !clientRecord || isArchivingClientRecord;

    return (
        <Stack vertical fullWidth style={{ minHeight: 200 }}>
            <AnimateLoadedContent isLoading={isLoading}>
                {() =>
                    clientRecord && clientRecordStatusTypeGuard.notInvited(clientRecord) ? (
                        <Content
                            clientRecord={clientRecord}
                            onReEngageClientRecord={() => onArchive(organizationClientRecordId!)}
                            onClose={onClose}
                        />
                    ) : null
                }
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(ArchiveClientPromptContent);
